import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@/shared/lib";

import { isClipReady, isClipWorking } from "../../model/clipState";
import { formatClock } from "../../model/formatClock";
import type { HighlightView } from "../../model/Highlight";
import { useHighlightTitle } from "../HighlightParts";

const LABEL = msg`Moments on the match timeline`;
const MARK = msg`{title} at {time}`;

type MatchTimelineProps = {
  durationS: number;
  items: HighlightView[];
  selectedId: number | null;
  onPick: (highlight: HighlightView) => void;
};

// Полоса матча с отметками моментов (доска 18д): где в матче случился каждый момент. Готовый
// клип — закрашенная отметка, в записи — золотое кольцо, без клипа — светлое кольцо, лучший
// момент крупнее.
export const MatchTimeline = ({ durationS, items, selectedId, onPick }: MatchTimelineProps) => {
  const { i18n } = useLingui();
  const title = useHighlightTitle();
  return (
    <div className="flex flex-col gap-0.5">
      <div role="group" aria-label={i18n._(LABEL)} className="relative mx-4 h-8">
        <span
          aria-hidden="true"
          className="absolute inset-x-0 top-[15px] h-0.5 rounded bg-border"
        />
        {items.map((highlight) => {
          const label = i18n._({
            ...MARK,
            values: { title: title(highlight), time: formatClock(highlight.game_time_s) },
          });
          const share = Math.min(1, Math.max(0, highlight.game_time_s / durationS));
          return (
            <button
              key={highlight.id}
              type="button"
              aria-label={label}
              title={label}
              onClick={() => {
                onPick(highlight);
              }}
              style={{ left: `${share * 100}%` }}
              className="group absolute top-0 flex size-8 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            >
              <span
                className={cn(
                  "block rounded-full border-2 transition-transform group-hover:scale-125",
                  highlight.best ? "size-3.5" : "size-[11px]",
                  isClipReady(highlight)
                    ? "border-accent bg-accent"
                    : isClipWorking(highlight)
                      ? "border-accent bg-background"
                      : "border-muted bg-background",
                  selectedId === highlight.id && "ring-4 ring-accent/35",
                )}
              />
            </button>
          );
        })}
      </div>
      <span
        aria-hidden="true"
        className="flex justify-between px-1.5 font-mono text-xs text-subtle"
      >
        <span>0:00</span>
        <span>{formatClock(durationS)}</span>
      </span>
    </div>
  );
};
